// src/components/CostAnalysisPanel.jsx
import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { inIndia } from '../utils/siteRegion';

const API_BASE =
  '';

// --- helpers ---------------------------------------------------------------
const fmtCr = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};
const fmtFull = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

// map a BOQ section name to one of the 4 visual buckets
const bucketFor = (sectionName) => {
  const n = (sectionName || '').toUpperCase();
  if (/FRAME|STRUCTURE|RCC|PEB|CONCRETE/.test(n)) return 'structure';
  if (/BRICK|PLASTER|MASONRY|WALL|AAC|CIVIL/.test(n)) return 'civil';
  if (/ELECTRIC|PLUMB|HVAC|MEP|FIRE/.test(n)) return 'services';
  return 'finishing';
};

const BUCKETS = [
  { key: 'structure', label: 'Structure', color: '#378ADD' },
  { key: 'civil', label: 'Civil / masonry', color: 'var(--ok)' },
  { key: 'finishing', label: 'Finishing / interior', color: '#EF9F27' },
  { key: 'services', label: 'Services (MEP / HVAC)', color: '#7F77DD' },
];

// interior tier ₹/sqft bands (placeholder — from villa/exp-centre data later)
const INTERIOR_TIERS = {
  budget: { low: 600, high: 1000, label: 'Budget' },
  mid: { low: 1000, high: 1800, label: 'Mid' },
  premium: { low: 1800, high: 3200, label: 'Premium' },
};

const BUILDING_TYPES = [
  { v: 'residential_apartment', t: 'Residential apartment' },
  { v: 'villa', t: 'Villa' },
  { v: 'commercial', t: 'Commercial' },
  { v: 'industrial', t: 'Industrial' },
  { v: 'hospitality', t: 'Hospitality', disabled: true },
];
const TECHNIQUES = [
  { v: 'rcc_brick', t: 'RCC + Brick' },
  { v: 'rcc_aac', t: 'RCC + AAC' },
  { v: 'rcc_steel', t: 'RCC + Steel' },
  { v: 'steel', t: 'Steel' },
  { v: 'wood', t: 'Wood', disabled: true },
];

function Bar({ label, amount, pct, color }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 500 }}>{fmtCr(amount)} · {Math.round(pct)}%</span>
      </div>
      <div style={{ height: 8, background: 'var(--surface-1,#f1efe8)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color }} />
      </div>
    </div>
  );
}

export default function CostAnalysisPanel({ massing, project }) {
  const [buildingType, setBuildingType] = useState('residential_apartment');
  const [technique, setTechnique] = useState('rcc_brick');
  const [interiorTier, setInteriorTier] = useState('mid');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [boqOpen, setBoqOpen] = useState(false);

  const areaSqft = massing?.builtUpAreaSqft || 0;

  useEffect(() => {
    setBuildingType('residential_apartment');
    setTechnique('rcc_brick');
    setInteriorTier('mid');
    setBoqOpen(false);
    setData(null);
  }, [project?.id]);

  // live fetch whenever inputs change
  useEffect(() => {
    if (!areaSqft) return;
    let cancelled = false;
    setBusy(true); setErr('');
    const q = `type=${buildingType}&technique=${technique}&areaSqft=${areaSqft}`;
    fetch(`${API_BASE}/api/feasibility/estimate?${q}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) { d.ok ? setData(d) : setErr(d.error || 'Estimate failed'); } })
      .catch((e) => { if (!cancelled) setErr(String(e.message || e)); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [buildingType, technique, areaSqft]);

  // combine construction (from API sections) + interior (from tier) into buckets
  const model = useMemo(() => {
    if (!data) return null;
    const buckets = { structure: [0, 0], civil: [0, 0], finishing: [0, 0], services: [0, 0] };
    (data.sections || []).forEach((s) => {
      const b = bucketFor(s.name);
      buckets[b][0] += s.subtotalLow || 0;
      buckets[b][1] += s.subtotalHigh || 0;
    });
    // interior tier adds to finishing
    const tier = INTERIOR_TIERS[interiorTier];
    buckets.finishing[0] += areaSqft * tier.low;
    buckets.finishing[1] += areaSqft * tier.high;

    const low = Object.values(buckets).reduce((a, x) => a + x[0], 0);
    const high = Object.values(buckets).reduce((a, x) => a + x[1], 0);
    const mid = (low + high) / 2;
    return { buckets, low, high, mid };
  }, [data, interiorTier, areaSqft]);

  if (!massing) {
    return (
      <div style={{ padding: '1rem', fontSize: 13, color: 'var(--text-secondary,#c3c9d4)' }}>
        Generate the massing first (left panel → Build Up) to see cost analysis.
      </div>
    );
  }

  const card = {
    background: 'var(--surface-2,#1a2234)', border: '0.5px solid var(--border,#3a4456)',
    borderRadius: 12, padding: 16, marginBottom: 12,
  };
  const selStyle = { width: '100%', padding: '8px 10px', fontSize: 13,
    border: '0.5px solid var(--border-strong,#3a4456)', borderRadius: 8,
    background: 'var(--surface-2)', color: 'var(--text)' };

  const downloadBOQ = () => {
    if (!data || !model) return;

    const rows = [];
    // header context
    rows.push(['VAUOM — Cost Feasibility BOQ']);
    rows.push(['Building type', BUILDING_TYPES.find((b) => b.v === buildingType)?.t]);
    rows.push(['Technique', TECHNIQUES.find((t) => t.v === technique)?.t]);
    rows.push(['Built-up area (sqft)', areaSqft]);
    rows.push(['Basis', data.reference || 'Tire 2 City, India']);
    rows.push(['Interior tier', `${interiorTier} (₹${INTERIOR_TIERS[interiorTier].low}–${INTERIOR_TIERS[interiorTier].high}/sqft)`]);
    rows.push([]);
    // total
    rows.push(['ESTIMATED TOTAL', `₹${Math.round(model.low).toLocaleString('en-IN')}`, `to`, `₹${Math.round(model.high).toLocaleString('en-IN')}`]);
    rows.push([]);
    // column header
    rows.push(['Section', 'Item', 'Unit', 'Qty', 'Amount Low (₹)', 'Amount High (₹)']);

    (data.sections || []).forEach((sec) => {
      const secName = sec.name.replace(/^SECTION\s*\d+\s*—\s*/i, '');
      (sec.items || []).forEach((it) => {
        rows.push([
          secName,
          it.description,
          it.unit,
          it.qty,
          Math.round(it.amountLow),
          Math.round(it.amountHigh),
        ]);
      });
      rows.push([`${secName} — SUBTOTAL`, '', '', '', Math.round(sec.subtotalLow), Math.round(sec.subtotalHigh)]);
      rows.push([]);
    });

    // flags / disclaimers
    if (data.flags?.length) {
      rows.push(['Notes / confidence:']);
      data.flags.forEach((f) => rows.push([f]));
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 26 }, { wch: 44 }, { wch: 8 }, { wch: 10 }, { wch: 16 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BOQ');

    const fname = `VAUOM_BOQ_${buildingType}_${technique}_${areaSqft}sqft.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* context strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
        color: 'var(--text-secondary,#b0b7c3)', background: 'var(--surface-1,#141a28)',
        padding: '8px 10px', borderRadius: 8, marginBottom: 4 }}>
        {BUILDING_TYPES.find((b) => b.v === buildingType)?.t} · {TECHNIQUES.find((t) => t.v === technique)?.t}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11,
        color: 'var(--text-muted,#8b92a0)', margin: '0 2px 12px' }}>
        <span>{massing.floors} floors · {areaSqft.toLocaleString()} sqft BUA</span>
        {/* What the rates actually are: boq_rates.json cites Gujarat contractor quotes
            and invoices from 2025–26, not a city-tier index. */}
        <span>Rates: Gujarat project quotes, 2025–26</span>
      </div>

      {/* Every site is priced from the same Indian rates. Say so when the site is
          elsewhere, rather than print rupee figures that do not apply there. */}
      {project?.centerLat != null && project?.centerLng != null && !inIndia(project.centerLat, project.centerLng) && (
        <div role="note" style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
          background: 'var(--bg-warning,#faeeda)', borderRadius: 8, padding: '8px 10px',
          fontSize: 12, color: 'var(--text-warning,#854f0b)', marginBottom: 12 }}>
          <span aria-hidden="true">⚠</span>
          <div>This site is outside India. The estimate uses Indian (Gujarat) rates and does not apply to its location.</div>
        </div>
      )}

      {err && <div style={{ ...card, color: 'var(--text-danger,#b00)' }}>{err}</div>}

      {/* headline */}
      {model && (
        <div style={card}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)', marginBottom: 4 }}>
            Estimated total cost {busy && '· updating…'}
          </div>
          <div style={{ fontSize: 26, fontWeight: 500 }}>{fmtCr(model.low)} – {fmtCr(model.high)}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary,#c3c9d4)', marginTop: 2 }}>
            most likely <span style={{ fontWeight: 500, color: 'var(--text-primary,#e5e7eb)' }}>{fmtCr(model.mid)}</span>
            {' · '}₹{Math.round(model.mid / areaSqft).toLocaleString()}/sqft
          </div>
        </div>
      )}

      {/* breakdown */}
      {model && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Where the cost goes</div>
          {BUCKETS.map((b) => {
            const mid = (model.buckets[b.key][0] + model.buckets[b.key][1]) / 2;
            const pct = model.mid > 0 ? (mid / model.mid) * 100 : 0;
            return <Bar key={b.key} label={b.label} amount={mid} pct={pct} color={b.color} />;
          })}
        </div>
      )}

      {/* adjust */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Adjust</div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)', marginBottom: 3 }}>Building type</div>
          <select style={selStyle} value={buildingType} onChange={(e) => setBuildingType(e.target.value)}>
            {BUILDING_TYPES.map((b) => <option key={b.v} value={b.v} disabled={b.disabled}>{b.t}{b.disabled ? ' (soon)' : ''}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)', marginBottom: 3 }}>Construction technique</div>
          <select style={selStyle} value={technique} onChange={(e) => setTechnique(e.target.value)}>
            {TECHNIQUES.map((t) => <option key={t.v} value={t.v} disabled={t.disabled}>{t.t}{t.disabled ? ' (soon)' : ''}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)', marginBottom: 3 }}>Interior tier</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {Object.entries(INTERIOR_TIERS).map(([k, t]) => (
              <button key={k} onClick={() => setInteriorTier(k)} style={{
                flex: 1, textAlign: 'center', fontSize: 12, padding: '6px 0', cursor: 'pointer',
                borderRadius: 8,
                border: `0.5px solid ${interiorTier === k ? 'var(--border-accent,#378add)' : 'var(--border,#2c3444)'}`,
                background: interiorTier === k ? 'var(--bg-accent,#e6f1fb)' : 'transparent',
                color: interiorTier === k ? 'var(--text-accent,#7fb4f0)' : 'var(--text-secondary,#c3c9d4)',
                fontWeight: interiorTier === k ? 500 : 400,
              }}>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* confidence strip */}
      {data?.flags?.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
          background: 'var(--bg-warning,#faeeda)', borderRadius: 8, padding: '8px 10px',
          fontSize: 12, color: 'var(--text-warning,#854f0b)', marginBottom: 12 }}>
          <span>⚠</span>
          <div>{data.flags.map((f, i) => <div key={i}>{f}</div>)}</div>
        </div>
      )}
      {interiorTier && (
        <div style={{ fontSize: 11, color: 'var(--text-muted,#9aa3b2)', margin: '-6px 2px 12px' }}>
          Interior tier is an editable placeholder (₹{INTERIOR_TIERS[interiorTier].low}–{INTERIOR_TIERS[interiorTier].high}/sqft) — will be refined from real project data.
        </div>
      )}

      {/* BOQ */}
      {data && (
        <div style={{ border: '0.5px solid var(--border,#3a4456)', borderRadius: 12, overflow: 'hidden' }}>
          <div onClick={() => setBoqOpen((o) => !o)} style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', padding: '12px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            Detailed BOQ <span style={{ color: 'var(--text-secondary,#c3c9d4)' }}>{boqOpen ? '▲' : '▼'}</span>
          </div>
          {boqOpen && (data.sections || []).map((s, i) => (
            <div key={i}>
              <div style={{ borderTop: '0.5px solid var(--border,#eee)', padding: '10px 14px',
                fontSize: 12, display: 'flex', justifyContent: 'space-between', fontWeight: 500 }}>
                <span>{s.name.replace(/^SECTION\s*\d+\s*—\s*/i, '')}</span>
                <span>{fmtFull((s.subtotalLow + s.subtotalHigh) / 2)}</span>
              </div>
              {(s.items || []).slice(0, 30).map((it, j) => (
                <div key={j} style={{ padding: '4px 14px 4px 22px', fontSize: 11,
                  color: 'var(--text-secondary,#777)', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ maxWidth: '65%' }}>{it.description}</span>
                  <span>{fmtFull((it.amountLow + it.amountHigh) / 2)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {data && (
        <button type="button" onClick={downloadBOQ}
          style={{ width: '100%', padding: '10px', marginTop: 12, cursor: 'pointer',
            border: '0.5px solid var(--border-strong,#bbb)', borderRadius: 8,
            background: 'var(--surface-1,#f7f7f5)', fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          ⬇ Download BOQ (Excel)
        </button>
      )}
    </div>
  );
}
